import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

export default function MicButton({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)) setSupported(false);
    return () => {
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    };
  }, []);

  const stopTracks = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
  };

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopTracks();
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short — hold a moment while speaking"); return; }
        setBusy(true);
        try {
          const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data?.text) onTranscript?.(data.text, true);
          else toast.error("Couldn't hear anything — try again");
        } catch (err) {
          toast.error(formatError(err));
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied or unavailable");
    }
  }, [onTranscript]);

  const stop = useCallback(() => {
    try { mediaRecRef.current?.stop(); } catch {}
  }, []);

  if (!supported) {
    return (
      <Button data-testid="mic-btn-unsupported" size="icon" variant="outline" disabled title="Voice input not supported in this browser">
        <MicOff className="w-4 h-4" />
      </Button>
    );
  }
  return (
    <Button
      type="button"
      data-testid="mic-btn"
      size="icon"
      variant={recording ? "default" : "outline"}
      className={recording ? "bg-red-600 hover:bg-red-500 text-white emergency-pulse" : ""}
      onClick={recording ? stop : start}
      disabled={disabled || busy}
      title={recording ? "Stop & transcribe (Whisper AI)" : "Speak — Whisper AI will transcribe"}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </Button>
  );
}
